import React, { useEffect, useState } from 'react';
import { Modal, TextField } from '@mui/material';
import { useMutation } from '@apollo/client';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CHANGE_PASSWORD } from 'api';
import { getValidationSchema } from 'utils/validationUpdatePasswordSchemaGenerator';
import PartialLoader from 'components/layout/PartialLoader';
import ModalContainer from 'components/ModalContainer';
import { Button, openNotification } from 'tt-ui-lib/core';
import styles from './PasswordModal.module.scss';

const PASSWORD_UPDATE_FORM_ID = 'passwordUpdate';

const PasswordModal = ({ isModelOpen, handleClose, setIsOpen }) => {
  const [oldPasswordError, setOldPasswordError] = useState(null);
  const [isOldPasswordInValid, setIsOldPasswordInValid] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [isCurrentPasswordHidden, setIsCurrentPasswordHidden] = useState(false);
  const [isNewPasswordHidden, setIsNewPasswordHidden] = useState(false);
  const [isConfirmPasswordHidden, setIsConfirmPasswordHidden] = useState(false);

  const validationSchema = getValidationSchema();
  const formOptions = { resolver: yupResolver(validationSchema) };
  const formMethods = useForm(formOptions, { mode: 'all' });

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = formMethods;

  const [updatePassword] = useMutation(CHANGE_PASSWORD);

  const onSubmit = async (data) => {
    try {
      setIsUpdateLoading(true);
      const res = await updatePassword({ variables: { input: data } });
      if (res?.data?.updatePassword?.status === 'ERROR') {
        setIsOldPasswordInValid(true);
        setOldPasswordError(res.data.updatePassword.message);
      } else if (res?.data?.updatePassword?.status !== 'ERROR' && res.data) {
        setIsOldPasswordInValid(false);
        setOldPasswordError(null);
        setIsUpdateLoading(false);
        setIsOpen(false);
        openNotification({
          message: 'Password changed successfully.',
          type: 'success',
        });
      }
      setIsUpdateLoading(false);
    } catch (e) {
      console.log(e);
      setIsUpdateLoading(false);
    }
  };

  useEffect(() => {
    setIsOldPasswordInValid(false);
    setOldPasswordError(null);
  }, [isModelOpen]);

  return (
    <>
      {isModelOpen && (
        <Modal
          open={isModelOpen}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          classes={{
            root: styles.modalRoot,
          }}
          BackdropProps={{ style: { backgroundColor: 'transparent' } }}
        >
          <ModalContainer
            className={styles.passwordModalWrapper}
            handleClose={() => handleClose(setIsOpen, isUpdateLoading)}
            isPasswordUpdateModal
            isScroll
          >
            <div className={styles.modalTitle}>Change your password</div>
            <FormProvider {...formMethods}>
              <form id={PASSWORD_UPDATE_FORM_ID} className={styles.formWrapper}>
                <div className={styles.inputWrapper}>
                  <Controller
                    name="old_password"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <div className={styles.passwordInputContainer}>
                        <TextField
                          className={styles.modal_input}
                          label="Current password"
                          type={isCurrentPasswordHidden ? 'text' : 'password'}
                          value={value}
                          onChange={onChange}
                          error={isOldPasswordInValid || !!errors.old_password}
                          helperText={oldPasswordError || errors.old_password?.message}
                        />
                        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                        <img
                          src={
                            isCurrentPasswordHidden
                              ? '/images/templates/icon-eye-off-grey.svg'
                              : '/images/templates/icon-eye-grey.svg'
                          }
                          alt=""
                          onClick={() => setIsCurrentPasswordHidden(!isCurrentPasswordHidden)}
                          className={styles.passwordEyeIcon}
                          style={{ width: 22, height: 16 }}
                        />
                      </div>
                    )}
                  />
                </div>
                <div className={styles.inputWrapper}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <div className={styles.passwordInputContainer}>
                        <TextField
                          className={styles.modal_input}
                          label="New password"
                          type={isNewPasswordHidden ? 'text' : 'password'}
                          onChange={onChange}
                          error={!!errors.password}
                          helperText={errors.password?.message}
                        />
                        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                        <img
                          src={
                            isNewPasswordHidden
                              ? '/images/templates/icon-eye-off-grey.svg'
                              : '/images/templates/icon-eye-grey.svg'
                          }
                          alt=""
                          onClick={() => setIsNewPasswordHidden(!isNewPasswordHidden)}
                          className={styles.passwordEyeIcon}
                          style={{ width: 22, height: 16 }}
                        />
                      </div>
                    )}
                  />
                </div>
                <div className={styles.inputWrapper}>
                  <Controller
                    name="password_confirmation"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <div className={styles.passwordInputContainer}>
                        <TextField
                          className={styles.modal_input}
                          label="Confirm new password"
                          type={isConfirmPasswordHidden ? 'text' : 'password'}
                          onChange={onChange}
                          error={!!errors.password_confirmation}
                          helperText={errors.password_confirmation?.message}
                        />
                        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                        <img
                          src={
                            isConfirmPasswordHidden
                              ? '/images/templates/icon-eye-off-grey.svg'
                              : '/images/templates/icon-eye-grey.svg'
                          }
                          alt=""
                          onClick={() => setIsConfirmPasswordHidden(!isConfirmPasswordHidden)}
                          className={styles.passwordEyeIcon}
                          style={{ width: 22, height: 16 }}
                        />
                      </div>
                    )}
                  />
                </div>
              </form>
            </FormProvider>
            <Button
              type="primary"
              className={styles.changeBtn}
              form={PASSWORD_UPDATE_FORM_ID}
              onClick={handleSubmit(onSubmit)}
            >
              Change password
            </Button>
            <PartialLoader loading={isUpdateLoading} isLightContainer />
          </ModalContainer>
        </Modal>
      )}
    </>
  );
};

export default PasswordModal;
